import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import ArrivalNotifyDialog from "@/components/ArrivalNotifyDialog";
import { ArrowRight, Car } from "@phosphor-icons/react";
import { fmtDay, isoDay, ivColor } from "./dateHelpers";
import { CategorieBadge } from "@/components/InterventionCategoryPicker";
import { haversineKm, fmtDistance } from "@/lib/geo";

function distBetweenIvs(prev, curr, chantiersMap) {
  if (!prev || !curr || !chantiersMap) return null;
  if (prev.chantier_id && curr.chantier_id && prev.chantier_id === curr.chantier_id) return null;
  const a = chantiersMap[prev.chantier_id];
  const b = chantiersMap[curr.chantier_id];
  if (!a || !b) return null;
  return haversineKm(a.latitude, a.longitude, b.latitude, b.longitude);
}

export default function ListView({ days, interventions, team, refreshKey, chantiersMap }) {
  const nav = useNavigate();
  const [notifyIv, setNotifyIv] = useState(null);
  const [clients, setClients] = useState({});

  useEffect(() => {
    (async () => {
      const cl = await api.get("/clients").catch(() => ({ data: [] }));
      setClients(Object.fromEntries((cl.data || []).map((c) => [c.id, c])));
    })();
  }, [refreshKey]);

  const byDay = days.map((d) => {
    const iso = isoDay(d);
    const items = interventions
      .filter((x) => (x.scheduled_date || "").slice(0, 10) === iso)
      .sort((a, b) => {
        const ta = (a.scheduled_date || "").slice(11, 16);
        const tb = (b.scheduled_date || "").slice(11, 16);
        if (ta !== tb) return ta.localeCompare(tb);
        return (a.sort_order || 0) - (b.sort_order || 0);
      });
    return { day: d, items };
  });
  const teamMap = Object.fromEntries(team.map((u) => [u.id, u]));

  return (
    <div className="space-y-3">
      {byDay.map(({ day, items }) => (
        <div key={day.toISOString()}>
          <div className="text-[11px] uppercase tracking-widest text-white/50 mb-2">{fmtDay(day)}</div>
          {items.length === 0 ? (
            <div className="rounded-xl bg-white/[0.02] border border-white/5 p-3 text-xs text-white/30">Aucune intervention</div>
          ) : (
            <div className="space-y-2">
              {items.map((iv, idx) => {
                const u = teamMap[iv.technician_id];
                const ch = (chantiersMap || {})[iv.chantier_id];
                const prev = idx > 0 ? items[idx - 1] : null;
                const samePrevTech = prev && prev.technician_id === iv.technician_id;
                const km = samePrevTech ? distBetweenIvs(prev, iv, chantiersMap) : null;
                const color = ivColor(iv, chantiersMap, u);
                return (
                  <div key={iv.id}>
                    {km != null && (
                      <div className="flex items-center gap-1.5 pl-3 pb-1 text-[10px] text-white/40 font-mono tabular-nums" data-testid={`list-dist-${iv.id}`}>
                        <ArrowRight size={10} weight="bold" className="text-white/30" />
                        <span>{fmtDistance(km)}</span>
                        {u && <span className="text-white/20">· {u.name}</span>}
                      </div>
                    )}
                    <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3 hover:border-white/25 hover:bg-white/[0.05] transition-all shadow-sm hover:shadow-md" data-testid={`iv-${iv.id}`}>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => iv.chantier_id && nav(`/chantiers/${iv.chantier_id}`)}
                          className="flex-1 flex items-center gap-3 min-w-0 text-left active:scale-[0.99]"
                          data-testid={`iv-open-${iv.id}`}
                        >
                          <div className="w-1 h-10 rounded-full shrink-0" style={{ background: color }} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <CategorieBadge value={iv.categorie} size="xs" />
                              <div className="font-medium truncate flex-1">{iv.title}</div>
                            </div>
                            <div className="text-xs text-white/50 truncate">
                              {new Date(iv.scheduled_date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} · {iv.duration_min} min · {u?.name || "—"}
                              {ch && <span className="ml-1 text-white/40">· {ch.name}</span>}
                              {iv.last_arrival_notice_at && <span className="text-emerald-400 ml-2">• Client prévenu</span>}
                            </div>
                          </div>
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setNotifyIv({ iv, ch }); }}
                          className="rounded-full bg-emerald-500/15 border border-emerald-500/30 hover:bg-emerald-500/25 p-2 active:scale-95 shrink-0 transition-colors"
                          title="Prévenir le client de mon arrivée"
                          data-testid={`notify-${iv.id}`}
                        >
                          <Car size={14} className="text-emerald-400" weight="duotone" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
      <ArrivalNotifyDialog
        open={!!notifyIv}
        onClose={() => setNotifyIv(null)}
        intervention={notifyIv?.iv}
        chantier={notifyIv?.ch}
        client={notifyIv?.ch ? clients[notifyIv.ch.client_id] : null}
      />
    </div>
  );
}
